import React, { useMemo } from "react";
import { observer } from "mobx-react-lite";

import { useRootStore } from "../../../../store/contexts";
import { useDataGridHandlers } from "../../../../common/hooks";
import {
  DataGrid,
  DataGridColumn,
  DataGridRowAction,
} from "../../../../common/controls/lists/DataGrid";
import { DashboardLayoutTemplate } from "../../../../api/graphql";
import { DashboardLayoutTemplateListStore } from "../../store";

import {
  VisibilityIcon,
  EditIcon,
  DeleteForeverIcon,
  ContentCopyIcon,
} from "../../../../icons";

export type GridExpressProps = { listStore?: DashboardLayoutTemplateListStore };

export const DashboardLayoutTemplateListForm: React.FC<GridExpressProps> =
  observer(({ listStore }) => {
    const rootStore = useRootStore();
    const { langStore } = rootStore;

    if (!listStore) {
      return null;
    }

    const store = listStore;

    // Handlers de DataGrid (extraídos a hook común)
    const {
      handleColumnVisibilityChange,
      handleColumnOrderChange,
      handleColumnSizingChange,
      handleFiltersChange,
      handleSortingChange,
      handlePaginationChange,
    } = useDataGridHandlers(store, rootStore);

    // Row actions
    const rowActions: DataGridRowAction<DashboardLayoutTemplate>[] = useMemo(
      () => [
        {
          label: langStore.viewCommand,
          onClick: (row: DashboardLayoutTemplate, event?: React.MouseEvent) =>
            store.viewRow(row.id, event, "menu"),
          icon: <VisibilityIcon fontSize="small" />,
        },
        {
          label: langStore.editCommand,
          onClick: (row: DashboardLayoutTemplate, event?: React.MouseEvent) =>
            store.editRow(row.id, event, "menu"),
          icon: <EditIcon fontSize="small" />,
          isEnabled: () => store.isActionEnabled("update"),
        },
        {
          label: langStore.deleteCommand,
          onClick: (row: DashboardLayoutTemplate) => store.deleteRow(row.id),
          icon: <DeleteForeverIcon fontSize="small" />,
          isEnabled: () => store.isActionEnabled("delete"),
        },
        {
          label: langStore.duplicateCommand,
          onClick: (row: DashboardLayoutTemplate, event?: React.MouseEvent) =>
            store.duplicateRow(row.id, event, "menu"),
          icon: <ContentCopyIcon fontSize="small" />,
          isEnabled: () => store.isActionEnabled("create"),
        },
      ],
      [langStore, store]
    );

    // Columnas del store con accessorFn para referencias
    const columns = useMemo(() => {
      const storeColumns = store.grid
        .dataGridColumns as unknown as DataGridColumn<DashboardLayoutTemplate>[];
      return storeColumns.map((col) => {
        return col;
      });
    }, [store.grid.dataGridColumns]);

    const rows = store.dashboardLayoutTemplateCurrentPage || [];

    return (
      <div id={"ListOfDashboardLayoutTemplate"}>
        <div id={"StartOfListingOfDashboardLayoutTemplate"} />
        <DataGrid
          columns={columns}
          data={rows}
          isLoading={store.status === "fetching"}
          enableFiltering
          enableShowHideFilters
          onFiltersChange={handleFiltersChange}
          initialFilters={store.grid.filtering}
          sorting={store.grid.sorting.map((s) => ({
            id: s.columnName,
            desc: s.direction === "desc",
          }))}
          onSortingChange={handleSortingChange}
          pagination={{
            pageIndex: store.currentPage - 1,
            pageSize: store.pageSize,
          }}
          onPaginationChange={handlePaginationChange}
          rowCount={store.totalRows + (store.hasMorePages ? 1 : 0)}
          manualPagination
          enableColumnOrdering
          enableColumnResizing
          enableHiding
          columnVisibility={store.grid.mrtColumnVisibility}
          onColumnVisibilityChange={handleColumnVisibilityChange}
          columnOrder={store.grid.columnOrder}
          onColumnOrderChange={handleColumnOrderChange}
          columnSizing={store.grid.mrtColumnSizing}
          onColumnSizingChange={handleColumnSizingChange}
          showColumnFilters={store.grid.showColumnFilters}
          onShowColumnFiltersChange={(value) =>
            store.grid.setShowColumnFilters(value)
          }
          isFullScreen={store.grid.isFullScreen}
          onIsFullScreenChange={(value) => store.grid.setIsFullScreen(value)}
          getRowId={(row) => row.id}
          language={langStore.language as "es" | "en" | "pt"}
          onRowClick={(row, event) => store.handleRowClick(row, event)}
          selectedRowId={store.selectedRowId}
          rowActions={rowActions}
          muiTableBodyRowProps={() => ({
            sx: { cursor: "pointer" },
          })}
        />
        <div id={"EndOfListing"} />
      </div>
    );
  });
